import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BookOpen } from "lucide-react";
import api from "../lib/api";

/**
 * Iter XX.74 — My Journey.
 *
 * The visible artifact of the coaching loop. Deliberately minimal:
 *   • "Working on now" — the currently-active commitment
 *   • "Journey"        — chronological list of past commitments,
 *                        each with a Gully-voice one-liner
 *
 * No stats. No charts. No streaks. No filters. If you're tempted to
 * add anything here that reads like analytics, stop.
 *
 * Route is auth-guarded server-side (/api/journey/me → 401 without a
 * token). Client mirrors that: unauth → bounce to /chat.
 */

// ------- Relative time ("2 days ago", "just now") -------------------
function _relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const secs = (Date.now() - d.getTime()) / 1000;
  if (secs < 60) return "just now";
  if (secs < 60 * 60) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 60 * 60 * 24) return `${Math.floor(secs / 3600)}h ago`;
  const days = Math.floor(secs / 86400);
  if (days < 7) return days === 1 ? "yesterday" : `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 6) return weeks === 1 ? "last week" : `${weeks} weeks ago`;
  const months = Math.floor(days / 30);
  return months === 1 ? "last month" : `${months} months ago`;
}

export default function Journey() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    working_on: null,
    entries: [],
    has_journey: false,
  });

  useEffect(() => {
    let cancelled = false;
    api.get("/journey/me")
      .then((r) => {
        if (cancelled) return;
        setData(r?.data || { working_on: null, entries: [], has_journey: false });
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 401) {
          navigate("/chat");
          return;
        }
        setError("Couldn't load your journey right now.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [navigate]);

  return (
    <div
      className="min-h-screen bg-[#0a0a0a] text-neutral-100"
      data-testid="journey-screen"
    >
      <header
        className="px-5 py-4 flex items-center gap-3 border-b border-neutral-900"
        style={{ paddingTop: "calc(1rem + env(safe-area-inset-top, 0px))" }}
      >
        <button
          onClick={() => navigate("/chat")}
          className="p-1.5 -ml-1.5 rounded-lg hover:bg-neutral-900 transition-colors"
          data-testid="journey-back"
          aria-label="Back"
        >
          <ArrowLeft size={20} />
        </button>
        <h1
          className="text-lg font-semibold tracking-tight"
          data-testid="journey-title"
        >
          Journey
        </h1>
      </header>

      <main className="px-5 py-6 max-w-2xl mx-auto">
        {loading && (
          <div
            className="text-sm text-neutral-500 py-16 text-center"
            data-testid="journey-loading"
          >
            One moment…
          </div>
        )}

        {!loading && error && (
          <div
            className="text-sm text-red-400 py-8"
            data-testid="journey-error"
          >
            {error}
          </div>
        )}

        {!loading && !error && !data.has_journey && (
          <div className="py-16" data-testid="journey-empty">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-300 flex items-center justify-center mb-6">
              <BookOpen size={22} />
            </div>
            <div className="text-lg text-neutral-100 leading-snug mb-3">
              This is where your story lives.
            </div>
            <div className="text-sm text-neutral-400 leading-relaxed">
              Come back after your next match — that&apos;s when it starts
              to build.
            </div>
          </div>
        )}

        {!loading && !error && data.has_journey && (
          <>
            {/* Working on now — only when there's an active commitment.
                Aged-out commitments (>30d) still appear in the feed
                below but not here. */}
            {data.working_on && (
              <section
                className="mb-10"
                data-testid="journey-working-on"
              >
                <div className="text-xs uppercase tracking-widest text-emerald-400/80 mb-3">
                  Working on now
                </div>
                <div className="text-2xl font-medium leading-snug text-neutral-50 mb-2"
                  data-testid="journey-working-on-text">
                  {data.working_on.raw_text}
                </div>
                <div className="text-sm text-neutral-500">
                  Committed {_relTime(data.working_on.created_at)}.
                </div>
              </section>
            )}

            {/* Journey feed */}
            <section data-testid="journey-feed">
              <div className="text-xs uppercase tracking-widest text-neutral-500 mb-4">
                Journey
              </div>
              <ol className="space-y-6">
                {data.entries.map((e) => (
                  <li
                    key={e.entry_id}
                    className="flex gap-4"
                    data-testid={`journey-entry-${e.entry_id}`}
                  >
                    <div className="shrink-0 w-16 pt-0.5">
                      <div className="text-xs text-neutral-500">
                        {_relTime(e.created_at)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0 border-l border-neutral-800 pl-4 -ml-px">
                      <p className="text-[15px] text-neutral-200 leading-relaxed">
                        {e.narrated_line || `You committed to: ${e.raw_text}.`}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
